import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Modal from 'react-modal'
import {
  AdminPermission,
  AdminVenueBreadcrumbDto,
  AuditLogDto,
  AuditLogEventType,
  VisitDto,
} from '@ivy-monorepo/shared'
import Table from '../../components/Table'
import Pagination from '../../components/Pagination'
import VisitMap from '../../components/VisitMap'
import VenueBreadcrumbModal from '../../components/modals/VenueBreadcrumbModal'
import { adminService } from '../../services/admin.service'
import { useSearch } from '../../contexts/SearchContext'
import { usePermissions } from '../../contexts/PermissionsContext'

type VenueActivityView = 'visits' | 'breadcrumbs' | 'intentions'

const USER_INTENTION_AUDIT_EVENT_TYPES = `${AuditLogEventType.SetUserIntention},${AuditLogEventType.ClearUserIntention}`

const VIEW_TABS: Array<{ key: VenueActivityView; label: string; permission: AdminPermission }> = [
  { key: 'visits', label: 'Visits', permission: AdminPermission.Visits },
  { key: 'breadcrumbs', label: 'Breadcrumbs', permission: AdminPermission.Breadcrumbs },
  { key: 'intentions', label: 'Intentions', permission: AdminPermission.Intentions },
]

function calculateOptimalPageSize(): number {
  const viewportHeight = window.innerHeight
  const rowHeight = 56
  const headerHeight = 220
  const paginationHeight = 60
  const availableHeight = viewportHeight - headerHeight - paginationHeight
  const calculatedPageSize = Math.floor(availableHeight / rowHeight)
  return Math.max(10, Math.min(100, calculatedPageSize))
}

const VenueActivityPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { searchValue } = useSearch()
  const { hasPermission, isLoading: isPermissionsLoading } = usePermissions()

  const [visits, setVisits] = useState<VisitDto[]>([])
  const [visitsPagination, setVisitsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [breadcrumbs, setBreadcrumbs] = useState<AdminVenueBreadcrumbDto[]>([])
  const [breadcrumbsPagination, setBreadcrumbsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [selectedVisit, setSelectedVisit] = useState<string | null>(null)
  const [visitDetail, setVisitDetail] = useState<VisitDto | null>(null)
  const [selectedBreadcrumbPublicId, setSelectedBreadcrumbPublicId] = useState<string | null>(null)
  const [intentionLogs, setIntentionLogs] = useState<AuditLogDto[]>([])
  const [intentionLogsPagination, setIntentionLogsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue)
  const isVisitPopupOperation = useRef(false)

  const visibleTabs = useMemo(
    () => (isPermissionsLoading ? [] : VIEW_TABS.filter(tab => hasPermission(tab.permission))),
    [hasPermission, isPermissionsLoading],
  )

  const requestedView = (searchParams.get('view') as VenueActivityView | null) ?? 'visits'
  const activeView = visibleTabs.some(tab => tab.key === requestedView)
    ? requestedView
    : (visibleTabs[0]?.key ?? 'visits')

  const currentPage = parseInt(searchParams.get('page') || '1')
  const urlPageSize = searchParams.get('pageSize')
  const currentPageSize = urlPageSize ? parseInt(urlPageSize) : calculateOptimalPageSize()
  const currentSortBy = searchParams.get('sortBy') || (activeView === 'visits' ? 'checkedInAt' : 'createdAt')
  const currentSortOrder = searchParams.get('sortOrder') || 'desc'
  const period = searchParams.get('period') || ''
  const isLegacyPeriodView = activeView === 'visits' && period.length > 0

  useEffect(() => {
    if (requestedView !== activeView && visibleTabs.length > 0) {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.set('view', activeView)
      nextParams.set('page', '1')
      setSearchParams(nextParams, { replace: true })
    }
  }, [activeView, requestedView, searchParams, setSearchParams, visibleTabs.length])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchValue(searchValue), 300)
    return () => clearTimeout(timer)
  }, [searchValue])

  useEffect(() => {
    if (activeView !== 'visits' || isVisitPopupOperation.current) {
      return
    }

    const fetchVisits = async () => {
      try {
        const response = await adminService.getVisits(
          currentPage,
          currentPageSize,
          debouncedSearchValue,
          currentSortBy,
          currentSortOrder,
          period,
        )

        response.visits.forEach(visit => {
          visit.venue.logoUrl = visit.venue.logoUrl ?? `/images/venues/${visit.venue.type?.toLowerCase()}.png`
        })

        setVisits(response.visits)
        setVisitsPagination(response.pagination)

        const popupId = searchParams.get('popup')
        if (popupId) {
          setSelectedVisit(popupId)
        }
      } catch (err) {
        console.log('Failed to fetch visits', err)
      }
    }

    fetchVisits()
  }, [
    activeView,
    currentPage,
    currentPageSize,
    debouncedSearchValue,
    currentSortBy,
    currentSortOrder,
    period,
    searchParams,
  ])

  const fetchBreadcrumbs = useCallback(
    async (overridePage?: number) => {
      const pageToLoad = overridePage ?? currentPage
      try {
        const response = await adminService.getVenueBreadcrumbs(
          pageToLoad,
          currentPageSize,
          debouncedSearchValue,
          currentSortBy,
          currentSortOrder,
        )
        setBreadcrumbs(response.breadcrumbs)
        setBreadcrumbsPagination(response.pagination)
      } catch (err) {
        console.log('Failed to fetch venue breadcrumbs', err)
      }
    },
    [currentPage, currentPageSize, debouncedSearchValue, currentSortBy, currentSortOrder],
  )

  useEffect(() => {
    if (activeView !== 'breadcrumbs') {
      return
    }

    fetchBreadcrumbs()
  }, [activeView, fetchBreadcrumbs])

  useEffect(() => {
    if (activeView !== 'intentions') {
      return
    }

    const fetchIntentions = async () => {
      try {
        const response = await adminService.getAuditLogs(
          currentPage,
          currentPageSize,
          debouncedSearchValue,
          currentSortOrder as 'asc' | 'desc',
          USER_INTENTION_AUDIT_EVENT_TYPES,
        )

        const normalized = response.auditLogs.map((log) => {
          const anyLog = log as any
          const data = anyLog?.data
          if (typeof data === 'string') {
            try {
              return { ...anyLog, data: JSON.parse(data) }
            } catch {
              return anyLog
            }
          }
          return anyLog
        }) as AuditLogDto[]

        setIntentionLogs(normalized)
        setIntentionLogsPagination(response.pagination)
      } catch (err) {
        console.log('Failed to fetch intention logs', err)
      }
    }

    fetchIntentions()
  }, [activeView, currentPage, currentPageSize, currentSortOrder, debouncedSearchValue])

  useEffect(() => {
    if (!selectedVisit) {
      setVisitDetail(null)
      return
    }

    const fetchVisitDetail = async () => {
      try {
        const detail = await adminService.getVisitDetail(selectedVisit)
        setVisitDetail(detail)
      } catch (err) {
        console.log('Failed to fetch visit detail', err)
        setVisitDetail(null)
      }
    }

    fetchVisitDetail()
  }, [selectedVisit])

  useEffect(() => {
    if (selectedVisit) {
      isVisitPopupOperation.current = true
    }
  }, [selectedVisit])

  const handleViewChange = (view: VenueActivityView) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('view', view)
    nextParams.set('page', '1')
    if (view !== 'visits') {
      nextParams.delete('period')
      nextParams.delete('popup')
    }
    setSearchParams(nextParams)
  }

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', page.toString())
    navigate(`?${newParams.toString()}`)
  }

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('sortBy', sortBy)
    newParams.set('sortOrder', sortOrder)
    newParams.set('page', '1')
    navigate(`?${newParams.toString()}`)
  }

  const handleVisitRowClick = (visit: VisitDto) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('popup', visit.publicId)
    navigate(`?${newParams.toString()}`)
    setSelectedVisit(visit.publicId)
  }

  const handleCloseVisitModal = () => {
    setSelectedVisit(null)
    isVisitPopupOperation.current = true
    setTimeout(() => {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('popup')
      navigate(`?${newParams.toString()}`, { replace: true })
      isVisitPopupOperation.current = false
    }, 300)
  }

  const pageTitle =
    isLegacyPeriodView && period === 'last-month'
      ? 'Visits in last month'
      : activeView === 'intentions'
        ? 'Venue intentions'
        : 'Venue activity'

  const showPagination =
    (activeView === 'visits' && visitsPagination.totalPages > 1) ||
    (activeView === 'breadcrumbs' && breadcrumbsPagination.totalPages > 1) ||
    (activeView === 'intentions' && intentionLogsPagination.totalPages > 1)

  const paginationProps =
    activeView === 'visits'
      ? { currentPage: visitsPagination.page, totalPages: visitsPagination.totalPages }
      : activeView === 'intentions'
        ? { currentPage: intentionLogsPagination.page, totalPages: intentionLogsPagination.totalPages }
        : { currentPage: breadcrumbsPagination.page, totalPages: breadcrumbsPagination.totalPages }

  const breadcrumbColumns = useMemo(
    () => [
      { label: '', accessor: 'author.imageUrl', type: 'avatar' },
      { label: 'From', accessor: 'author.firstName', type: 'fullName' },
      { label: 'Venue', accessor: 'venue.name', type: 'text' },
      { label: 'Reactions', accessor: 'reactionCount', type: 'number' },
      { label: 'When', accessor: 'createdAt', type: 'datetime' },
      { label: 'Message', accessor: 'content', type: 'string' },
      { label: 'Flagged', accessor: 'publicId', type: 'breadcrumbModerationFlags' },
    ],
    [],
  )

  return (
    <div className="users-container">
      <div className="users-header">
        <h2>{pageTitle}</h2>
        {showPagination && (
          <Pagination
            currentPage={paginationProps.currentPage}
            totalPages={paginationProps.totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <div className="tab-bar" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          {visibleTabs.map(tab => {
            const classNames = ['tab']
            if (tab.key === activeView) {
              classNames.push('active')
            }

            return (
              <button
                key={tab.key}
                type="button"
                className={classNames.join(' ')}
                onClick={() => handleViewChange(tab.key)}
              >
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      <div className="users-table-container">
        <div className="table-wrapper">
          {activeView === 'visits' && (
            <Table
              id="visits"
              name="Visits"
              keyField="publicId"
              initialSortColumn={currentSortBy}
              initialSortOrder={currentSortOrder}
              columns={[
                { label: '', accessor: 'user.imageUrl', type: 'userAvatar' },
                { label: 'User', accessor: 'user.lastName', type: 'fullNameAndTelephone' },
                { label: '', accessor: 'venue.logoUrl', type: 'venueAvatar' },
                { label: 'Venue', accessor: 'venue.name', type: 'nameAndAddress' },
                { label: 'Check-in', accessor: 'checkedInAt', type: 'datetime' },
                { label: 'Duration', accessor: 'secondsInVenue', type: 'interval' },
                { label: 'Checkout type', accessor: 'checkoutType', type: 'checkoutType' },
              ]}
              sourceData={visits}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={handleVisitRowClick}
              onSortChange={handleSortChange}
              disableGlobalSearch={true}
              disableClientSort={true}
            />
          )}

          {activeView === 'breadcrumbs' && (
            <Table
              id="venue-breadcrumbs"
              name="Venue Breadcrumbs"
              keyField="publicId"
              initialSortColumn={currentSortBy}
              initialSortOrder={currentSortOrder}
              columns={breadcrumbColumns}
              sourceData={breadcrumbs}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={(row: AdminVenueBreadcrumbDto) => setSelectedBreadcrumbPublicId(row.publicId)}
              onSortChange={handleSortChange}
              disableGlobalSearch={true}
              disableClientSort={true}
            />
          )}

          {activeView === 'intentions' && (
            <Table
              id="venue-intention-logs"
              name="Intentions"
              keyField="publicId"
              initialSortColumn="createdAt"
              initialSortOrder={currentSortOrder}
              columns={[
                { label: 'Set at', accessor: 'createdAt', type: 'datetime' },
                { label: 'User', accessor: 'affectedUserFullName', type: 'text' as any },
                { label: 'Intention', accessor: 'data.name', type: 'text' as any },
                { label: 'Prompt', accessor: 'data.prompt', type: 'text' as any },
                { label: 'Emoji', accessor: 'data.emoji', type: 'text' as any },
                { label: 'Venue', accessor: 'data.venueName', type: 'text' as any },
              ]}
              sourceData={intentionLogs}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={() => {}}
              onSortChange={handleSortChange}
              disableGlobalSearch={false}
              disableClientSort={true}
            />
          )}
        </div>
      </div>

      <Modal
        isOpen={!!selectedVisit && !!visitDetail}
        onRequestClose={handleCloseVisitModal}
        className="visit-map-popup"
        overlayClassName="modal-overlay"
        closeTimeoutMS={300}
      >
        {visitDetail && (
          <div className="visit-map-popup-content">
            <div className="visit-map-popup-map-container">
              <VisitMap
                latitude={visitDetail.venue.latitude}
                longitude={visitDetail.venue.longitude}
                northeastLatitude={visitDetail.venue.northeastLatitude}
                northeastLongitude={visitDetail.venue.northeastLongitude}
                southwestLatitude={visitDetail.venue.southwestLatitude}
                southwestLongitude={visitDetail.venue.southwestLongitude}
                pings={visitDetail.pings || null}
              />
            </div>
          </div>
        )}
      </Modal>

      <VenueBreadcrumbModal
        isOpen={!!selectedBreadcrumbPublicId}
        onClose={() => setSelectedBreadcrumbPublicId(null)}
        publicId={selectedBreadcrumbPublicId}
        onModerationChange={() => fetchBreadcrumbs()}
        onDelete={async breadcrumbPublicId => {
          await adminService.deleteVenueBreadcrumb(breadcrumbPublicId)
        }}
        onDeleted={() => {
          setSelectedBreadcrumbPublicId(null)

          const isLastItemOnPage = breadcrumbs.length === 1
          if (isLastItemOnPage && currentPage > 1) {
            const newParams = new URLSearchParams(searchParams)
            newParams.set('page', String(currentPage - 1))
            navigate(`?${newParams.toString()}`)
            return
          }

          fetchBreadcrumbs()
        }}
      />
    </div>
  )
}

export default VenueActivityPage
